// API for Page Access (/admin/access) and the User Manager
// (/admin/user/userManager). Server: server/routes/api/Access/accessHandler.js.
// Admin calls identify the caller by the x-user-id header (no token
// validation server-side — the same trust model as the Safety, Fleet and App
// Codes admin routes).

export const ACCESS_ROLES = ["Admin"];

const request = async (url, userId, { method = "GET", body } = {}) => {
  const res = await fetch(url, {
    method,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      "x-user-id": userId || "",
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  let content = {};
  try {
    content = await res.json();
  } catch (e) {
    // Non-JSON error page (e.g. the server is restarting).
  }
  if (!res.ok) throw new Error(content.error || "Request failed");
  return content;
};

// Any signed-in user, by their own id. `route` (the page they're on) feeds
// Currently Online; `redirect` is an admin's pending "send them to a page":
// { found, roles, branch, branchOverride, deactivated, grantedPaths,
//   redirect: { path, by, at } | null }
export const fetchSession = (id, route) =>
  request(
    `/api/access/session?id=${encodeURIComponent(id)}${
      route ? `&route=${encodeURIComponent(route)}` : ""
    }`
  );

// { users: [{ id, name, email, title, roles, branch, branch_override,
//   directory_branch, deactivated, last_seen, last_route, time_spent }] }
export const fetchAccessUsers = (userId) => request("/api/access/admin/users", userId);

// { grants: [{ key, name, roles, users, updatedAt, updatedBy }],
//   customRoles: [{ name, description, createdAt, createdBy }],
//   roleCounts: { [role]: users }, audit: [...] }
export const fetchAccessOverview = (userId) => request("/api/access/admin/overview", userId);

export const fetchAccessAudit = (userId, target) =>
  request(`/api/access/admin/audit?target=${encodeURIComponent(target || "")}`, userId);

// change: { addRoles?, removeRoles?, branch? (string = override, null = back
// to the directory), deactivated? } → { user, changed }
export const updateAccessUser = (userId, targetId, change) =>
  request(`/api/access/admin/users/${encodeURIComponent(targetId)}`, userId, {
    method: "PATCH",
    body: change,
  });

// → { users, skipped: [{ id, reason }] }
export const bulkUpdateAccessUsers = (userId, ids, change) =>
  request("/api/access/admin/users/bulk", userId, {
    method: "POST",
    body: { ids, ...change },
  });

// change: { key, name, redirect?, addRoles?, removeRoles?, addUsers?,
// removeUsers? } → { grant }
export const updatePageGrant = (userId, change) =>
  request("/api/access/admin/pages", userId, { method: "PATCH", body: change });

export const createCustomRole = (userId, name, description) =>
  request("/api/access/admin/roles", userId, {
    method: "POST",
    body: { name, description },
  });

export const editCustomRole = (userId, name, description) =>
  request(`/api/access/admin/roles/${encodeURIComponent(name)}`, userId, {
    method: "PATCH",
    body: { description },
  });

// → { ok, users, pages } — how many it was taken off.
export const deleteCustomRole = (userId, name) =>
  request(`/api/access/admin/roles/${encodeURIComponent(name)}`, userId, {
    method: "DELETE",
  });

// ---- Automation (User Manager → Automation) ----

// { directory: { settings, runs, graphConfigured, everyHours },
//   autoRoles: { enabled, rules } }
export const fetchAutomation = (userId) => request("/api/access/admin/automation", userId);

// patch: { mode?: "preview" | "auto", deactivateNoMailbox?, deactivateHidden?,
// reactivate?, protected?: [ids] } → { settings }
export const saveDirectorySettings = (userId, patch) =>
  request("/api/access/admin/automation/directory", userId, { method: "PUT", body: patch });

// Compare with Microsoft 365 now, change nothing → { run }
export const checkDirectory = (userId) =>
  request("/api/access/admin/automation/directory/check", userId, { method: "POST", body: {} });

// Apply the reviewed people (re-checked server-side) → { run }
export const applyDirectory = (userId, { deactivate = [], reactivate = [] }) =>
  request("/api/access/admin/automation/directory/apply", userId, {
    method: "POST",
    body: { deactivate, reactivate },
  });

// → { settings: { enabled, rules } }
export const saveAutoRoles = (userId, settings) =>
  request("/api/access/admin/automation/roles", userId, { method: "PUT", body: settings });

// → { matches: [{ _id, id, name, title, branch, roles, rules }] }
export const previewAutoRoles = (userId, rules) =>
  request("/api/access/admin/automation/roles/preview", userId, { method: "POST", body: { rules } });

// → { changed: [{ _id, id, name, roles }] }
export const applyAutoRolesNow = (userId) =>
  request("/api/access/admin/automation/roles/apply", userId, { method: "POST", body: {} });
